import { useEffect, useState } from "react";

import { RingProgress } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Text } from "components";
import { DashboardService } from "services";
import { DashboardKpiMeterResponse, DashboardKpiPeriod } from "types";

import classes from "./KpiSection.module.css";

interface KpiMetricDefinition {
  labelKey: string;
  meterTypes: readonly string[];
  color: string;
}

interface KpiMetricItem {
  labelKey: string;
  value: number;
  target: number;
  color: string;
}

interface KpiGroupDefinition {
  titleKey: string;
  period: DashboardKpiPeriod;
  metrics: readonly KpiMetricDefinition[];
}

interface KpiGroupItem {
  titleKey: string;
  metrics: KpiMetricItem[];
}

const DAILY_KPI_METRICS: readonly KpiMetricDefinition[] = [
  {
    labelKey: "dashboard.kpis.metrics.qualifications",
    meterTypes: ["QUALIFICATION"],
    color: "#b24545",
  },
  {
    labelKey: "dashboard.kpis.metrics.leads",
    meterTypes: ["BANK_NOW", "BANK_LEAD"],
    color: "#71bf44",
  },
];

const MONTHLY_KPI_METRICS: readonly KpiMetricDefinition[] = [
  {
    labelKey: "dashboard.kpis.metrics.approved",
    meterTypes: ["APPROVED"],
    color: "#d4c84a",
  },
  {
    labelKey: "dashboard.kpis.metrics.disbursed",
    meterTypes: ["DISBURSED"],
    color: "#b24545",
  },
] as const;

const KPI_GROUPS: readonly KpiGroupDefinition[] = [
  {
    titleKey: "dashboard.kpis.daily",
    period: "DAILY",
    metrics: DAILY_KPI_METRICS,
  },
  {
    titleKey: "dashboard.kpis.monthly",
    period: "MONTHLY",
    metrics: MONTHLY_KPI_METRICS,
  },
] as const;

const createEmptyMetrics = (metrics: readonly KpiMetricDefinition[]): KpiMetricItem[] =>
  metrics.map((metric) => ({
    labelKey: metric.labelKey,
    value: 0,
    target: 0,
    color: metric.color,
  }));

const createEmptyGroups = (): KpiGroupItem[] =>
  KPI_GROUPS.map((group) => ({
    titleKey: group.titleKey,
    metrics: createEmptyMetrics(group.metrics),
  }));

const mapKpiMetersToMetrics = (
  metrics: readonly KpiMetricDefinition[],
  meters: DashboardKpiMeterResponse[],
): KpiMetricItem[] => {
  const metersByType = meters.reduce<Record<string, DashboardKpiMeterResponse>>((acc, item) => {
    acc[item.type] = item;
    return acc;
  }, {});

  return metrics.map((metric) => {
    const data = metric.meterTypes.map((type) => metersByType[type]).find(Boolean);

    return {
      labelKey: metric.labelKey,
      value: typeof data?.value === "number" ? data.value : 0,
      target: typeof data?.target === "number" ? data.target : 0,
      color: metric.color,
    };
  });
};

const getProgressValue = (value: number, target: number) => {
  if (target <= 0) {
    return 0;
  }

  return Math.min((value / target) * 100, 100);
};

const KpiMetric = ({ labelKey, value, target, color }: KpiMetricItem) => {
  const { t } = useTranslation();

  return (
    <div className={classes.metric}>
      <div className={classes.ring}>
        <RingProgress
          size={82}
          thickness={6}
          roundCaps
          sections={[{ value: getProgressValue(value, target), color }]}
          rootColor="#d9dee4"
        />
        <div className={classes.ringValue}>{`${value}/${target}`}</div>
      </div>
      <Text className={classes.metricLabel}>{t(labelKey)}</Text>
    </div>
  );
};

const KpiSection = () => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<KpiGroupItem[]>(createEmptyGroups);

  useEffect(() => {
    let mounted = true;

    const loadKpis = async () => {
      const results = await Promise.allSettled(
        KPI_GROUPS.map((group) => DashboardService.getAgentKpis({ period: group.period })),
      );

      if (!mounted) {
        return;
      }

      setGroups(
        KPI_GROUPS.map((group, index) => {
          const result = results[index];
          const meters = result.status === "fulfilled" ? result.value?.meters ?? [] : [];

          return {
            titleKey: group.titleKey,
            metrics: mapKpiMetersToMetrics(group.metrics, meters),
          };
        }),
      );
    };

    loadKpis();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className={classes.root}>
      {groups.map((group) => (
        <section key={group.titleKey} className={classes.group}>
          <Text className={classes.headerLabel}>{t(group.titleKey)}</Text>

          <div className={classes.metrics}>
            {group.metrics.map((item) => (
              <KpiMetric key={item.labelKey} {...item} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default KpiSection;
